import React from 'react';
import { Palette, Heart, Check, Loader } from 'lucide-react';

const ProcessingDialog = ({
  imagePreview,
  detectColors,
  setDetectColors,
  detectSentiments,
  setDetectSentiments,
  onProcess,
  onSkip,
  isProcessing = false
}) => {
  return (
    <div className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
        <div className="p-6 border-b border-beige">
          <h3 className="text-xl font-medium text-charcoal">
            {isProcessing ? 'Processing...' : 'Smart Detection'}
          </h3>
          <p className="text-sm text-gray-soft mt-1">
            {isProcessing
              ? 'Analyzing your image for colors and sentiments'
              : 'Choose what to detect — these power search and AI design recommendations'}
          </p>
        </div>
        <div className="p-6">
          <div className="mb-6 flex justify-center">
            <img
              src={imagePreview}
              alt="Preview"
              className="max-h-32 rounded-lg shadow-sm"
            />
          </div>

          {isProcessing ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader className="animate-spin text-sage" size={36} />
              <p className="text-sm text-gray-soft">This may take a moment...</p>
            </div>
          ) : (
            <div className="flex gap-4 justify-center">
              <button
                type="button"
                onClick={() => setDetectColors(!detectColors)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 min-w-[140px] ${
                  detectColors
                    ? 'border-sage bg-sage/10 text-sage'
                    : 'border-beige bg-white text-gray-soft hover:border-sage/50'
                }`}
                data-testid="detect-colors-option"
              >
                <div className={`p-3 rounded-full ${detectColors ? 'bg-sage text-white' : 'bg-beige text-gray-soft'}`}>
                  <Palette size={24} />
                </div>
                <span className="font-medium text-sm">Color Detection</span>
                <p className="text-[10px] text-gray-soft text-center leading-tight">
                  Finds the 6 most prominent colors so you can search by color family
                </p>
                {detectColors && <Check size={16} className="text-sage" />}
              </button>

              <button
                type="button"
                onClick={() => setDetectSentiments(!detectSentiments)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 min-w-[140px] ${
                  detectSentiments
                    ? 'border-sage bg-sage/10 text-sage'
                    : 'border-beige bg-white text-gray-soft hover:border-sage/50'
                }`}
                data-testid="detect-sentiments-option"
              >
                <div className={`p-3 rounded-full ${detectSentiments ? 'bg-sage text-white' : 'bg-beige text-gray-soft'}`}>
                  <Heart size={24} />
                </div>
                <span className="font-medium text-sm">Sentiment Detection</span>
                <p className="text-[10px] text-gray-soft text-center leading-tight">
                  Reads text on your item to tag sentiments like "Thank You" or "Happy Birthday"
                </p>
                {detectSentiments && <Check size={16} className="text-sage" />}
              </button>
            </div>
          )}

          {!isProcessing && (
            <p className="text-[10px] text-gray-light text-center mt-4">
              Both help the Craft Companion recommend the right items for your projects
            </p>
          )}
        </div>
        {!isProcessing && (
          <div className="p-6 border-t border-beige flex justify-between">
            <button
              onClick={onSkip}
              className="px-4 py-2 text-gray-soft hover:text-charcoal transition-colors duration-200"
              data-testid="skip-processing-btn"
            >
              Skip Detection
            </button>
            <button
              onClick={onProcess}
              className="px-6 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors duration-200 flex items-center gap-2"
              data-testid="process-image-btn"
            >
              {(detectColors || detectSentiments) ? 'Analyze Image' : 'Continue'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessingDialog;
